import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crosshair, KeyRound } from "lucide-react";

const field = "bg-[#0d0d0d] border-[#27272a] text-white h-11 focus:ring-2 focus:ring-volt focus:border-volt";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (pw !== pw2) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. Log in with your new password.");
      setTimeout(() => navigate("/auth"), 1400);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative z-10 flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <Crosshair className="text-volt" size={26} strokeWidth={2.5} />
          <span className="font-heading font-black text-2xl uppercase">Spar<span className="text-volt">Base</span></span>
        </Link>

        <div className="bg-card border border-white/10 rounded-sm p-8">
          {!token ? (
            <div className="text-center">
              <KeyRound className="mx-auto text-[#FF3B30] mb-3" size={30} />
              <h2 className="font-heading text-2xl uppercase tracking-tight">Invalid link</h2>
              <p className="text-sm text-zinc-400 mt-2">This reset link is missing or malformed.</p>
              <Link to="/auth" className="inline-block mt-5 text-volt text-sm uppercase tracking-wider hover:underline">Back to log in</Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <KeyRound className="mx-auto text-volt mb-3" size={30} />
              <h2 className="font-heading text-2xl uppercase tracking-tight">Password reset</h2>
              <p className="text-sm text-zinc-400 mt-2">Redirecting you to log in…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-heading text-3xl uppercase tracking-tight">Set new password</h2>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">New Password</Label>
                <Input data-testid="reset-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={field} placeholder="min 6 chars" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Confirm Password</Label>
                <Input data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={field} placeholder="repeat password" />
              </div>
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-volt text-black font-heading font-bold uppercase tracking-widest text-lg py-3 rounded-sm transition-colors duration-200 hover:bg-volt-hover disabled:opacity-40"
              >
                {loading ? "Updating…" : "Update Password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
